import { useState, useCallback } from 'react'
import { ChatMessage } from '@/components/chat/ChatInterface'
import { AnchorMCPClient, createMCPClient, type CDPExecutionResult } from '@/lib/mcp-client'
import { useToast } from './use-toast'
import { api } from '@/lib/api'

export interface ChatWorkflowState {
  messages: ChatMessage[]
  isLoading: boolean
  mcpClient: AnchorMCPClient | null
  isConnected: boolean
  currentSession: {
    sessionId: string | null
    liveUrl: string | null
    status: string
  }
}

export function useChatWorkflow() {
  const [state, setState] = useState<ChatWorkflowState>({
    messages: [],
    isLoading: false,
    mcpClient: null,
    isConnected: false,
    currentSession: {
      sessionId: null,
      liveUrl: null,
      status: 'idle'
    }
  })
  const { toast } = useToast()

  // Initialize MCP client
  const initializeMCP = useCallback(async () => {
    try {
      const client = createMCPClient()
      const isConnected = await client.testConnection()

      setState(prev => ({
        ...prev,
        mcpClient: client,
        isConnected
      }))

      if (isConnected) {
        toast({
          title: "Connected",
          description: "Successfully connected to Anchor Browser MCP service",
        })
      } else {
        toast({
          title: "Connection Failed",
          description: "Failed to connect to MCP service. Please check your network connection.",
          variant: "destructive"
        })
      }

      return isConnected
    } catch (error) {
      console.error('Failed to initialize MCP client:', error)
      toast({
        title: "Error",
        description: "Failed to initialize MCP client",
        variant: "destructive"
      })
      return false
    }
  }, [toast])

  // Add message to chat
  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date()
    }

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage]
    }))

    return newMessage
  }, [])

  // Send user message and get AI response
  const sendMessage = useCallback(async (content: string) => {
    if (!state.mcpClient || !state.isConnected) {
      toast({
        title: "Not Connected",
        description: "Please connect to MCP service first",
        variant: "destructive"
      })
      return
    }

    // Add user message
    addMessage({
      role: 'user',
      content,
      type: 'text'
    })

    setState(prev => ({ ...prev, isLoading: true }))

    try {
      // Generate Playwright code using MCP
      const codeGeneration = await state.mcpClient.generatePlaywrightCode(content)
      
      // Add assistant response with generated code
      addMessage({
        role: 'assistant',
        content: codeGeneration.description,
        type: 'code',
        metadata: {
          playwrightCode: codeGeneration.code
        }
      })

    } catch (error) {
      console.error('Failed to generate response:', error)
      
      // Add error message
      addMessage({
        role: 'assistant',
        content: 'Sorry, I encountered an error while generating the Playwright code. Please try again with a different description.',
        type: 'text'
      })

      toast({
        title: "Error",
        description: "Failed to generate Playwright code",
        variant: "destructive"
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [state.mcpClient, state.isConnected, addMessage, toast])

  // Execute generated Playwright code in Anchor Browser session
  const executeCode = useCallback(async (code: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      // Create a new Anchor Browser session for code execution
      const sessionResponse = await api.sessions.createSession({
        browser_viewport_width: 1280,
        browser_viewport_height: 960,
        use_proxy: false,
        use_adblock: true
      })

      const session = sessionResponse

      // Update state with new session
      setState(prev => ({
        ...prev,
        currentSession: {
          sessionId: session.id,
          liveUrl: session.live_view_url || null,
          status: 'active'
        }
      }))

      addMessage({
        role: 'assistant',
        content: `🚀 Created browser session ${session.id}. Executing Playwright code...`,
        type: 'execution',
        metadata: {
          sessionId: session.id,
          playwrightCode: code
        }
      })

      // Convert Playwright code to natural language task for Anchor Browser
      const task = convertPlaywrightToTask(code)

      // Execute the task in the session using the tools API
      const taskResponse = await api.request(`/tools/perform-web-task?sessionId=${session.id}`, {
        method: 'POST',
        body: JSON.stringify({
          prompt: task,
          provider: 'openai',
          model: 'gpt-4o-mini'
        })
      })

      const result = taskResponse as any

      addMessage({
        role: 'assistant',
        content: `✅ Code executed successfully!\n\nResult: ${result.data?.result || 'Task completed'}`,
        type: 'execution',
        metadata: {
          sessionId: session.id,
          executionResult: result
        }
      })

      toast({
        title: "Code Executed",
        description: "Playwright code executed in browser session",
      })

    } catch (error) {
      console.error('Failed to execute code:', error)

      addMessage({
        role: 'assistant',
        content: `❌ Code execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'execution'
      })

      toast({
        title: "Execution Failed",
        description: "Failed to execute Playwright code",
        variant: "destructive"
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [addMessage, toast])

  // Execute Playwright code directly via CDP (new method)
  const executeCodeViaCDP = useCallback(async (code: string, sessionId?: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      addMessage({
        role: 'assistant',
        content: `🚀 ${sessionId ? `Using existing session ${sessionId}` : 'Creating new browser session'}. Executing Playwright code via CDP...`,
        type: 'execution',
        metadata: {
          sessionId: sessionId || 'new',
          playwrightCode: code,
          executionMethod: 'cdp'
        }
      })

      // Execute code via CDP using MCP client
      const result: CDPExecutionResult = await state.mcpClient!.executePlaywrightCodeViaCDP(
        code,
        sessionId,
        !sessionId, // createNewSession if no sessionId provided
        60000 // 60 second timeout
      )

      // Update state with session info
      setState(prev => ({
        ...prev,
        currentSession: {
          sessionId: result.sessionId,
          liveUrl: result.liveViewUrl,
          status: result.success ? 'active' : 'error'
        }
      }))

      if (result.success) {
        let resultMessage = `✅ Code executed successfully via CDP!\n\n`

        if (result.executionTime) {
          resultMessage += `⏱️ Execution time: ${result.executionTime}ms\n`
        }

        if (result.logs && result.logs.length > 0) {
          resultMessage += `\n📋 Console logs:\n${result.logs.slice(0, 5).map(log => `• ${log}`).join('\n')}`
          if (result.logs.length > 5) {
            resultMessage += `\n... and ${result.logs.length - 5} more logs`
          }
        }

        addMessage({
          role: 'assistant',
          content: resultMessage,
          type: 'execution',
          metadata: {
            sessionId: result.sessionId,
            executionResult: result,
            executionMethod: 'cdp'
          }
        })

        toast({
          title: "Code Executed via CDP",
          description: `Playwright code executed directly in browser session ${result.sessionId}`,
        })
      } else {
        addMessage({
          role: 'assistant',
          content: `❌ CDP execution failed: ${result.error || 'Unknown error'}`,
          type: 'execution',
          metadata: {
            sessionId: result.sessionId,
            executionResult: result,
            executionMethod: 'cdp'
          }
        })

        toast({
          title: "CDP Execution Failed",
          description: result.error || "Failed to execute Playwright code via CDP",
          variant: "destructive"
        })
      }

    } catch (error) {
      console.error('Failed to execute code via CDP:', error)

      addMessage({
        role: 'assistant',
        content: `❌ CDP execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'execution'
      })

      toast({
        title: "CDP Execution Failed",
        description: "Failed to execute Playwright code via CDP",
        variant: "destructive"
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [state.mcpClient, addMessage, toast])

  // Clear chat history
  const clearMessages = useCallback(() => {
    setState(prev => ({
      ...prev,
      messages: []
    }))
  }, [])

  // Get available MCP tools
  const getAvailableTools = useCallback(async () => {
    if (!state.mcpClient) return []
    
    try {
      return await state.mcpClient.listTools()
    } catch (error) {
      console.error('Failed to get MCP tools:', error)
      return []
    }
  }, [state.mcpClient])

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    isConnected: state.isConnected,
    currentSession: state.currentSession,
    sendMessage,
    executeCode,
    executeCodeViaCDP,
    clearMessages,
    initializeMCP,
    getAvailableTools
  }
}

/**
 * Convert Playwright code to natural language task for Anchor Browser
 */
function convertPlaywrightToTask(playwrightCode: string): string {
  // Extract key actions from Playwright code
  const lines = playwrightCode.split('\n')
  const actions: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()

    if (trimmed.includes('page.goto(')) {
      const urlMatch = trimmed.match(/page\.goto\(['"`]([^'"`]+)['"`]\)/)
      if (urlMatch) {
        actions.push(`Navigate to ${urlMatch[1]}`)
      }
    }

    if (trimmed.includes('page.fill(')) {
      const fillMatch = trimmed.match(/page\.fill\(['"`]([^'"`]+)['"`],\s*['"`]([^'"`]+)['"`]\)/)
      if (fillMatch) {
        actions.push(`Fill the field "${fillMatch[1]}" with "${fillMatch[2]}"`)
      }
    }

    if (trimmed.includes('page.click(')) {
      const clickMatch = trimmed.match(/page\.click\(['"`]([^'"`]+)['"`]\)/)
      if (clickMatch) {
        actions.push(`Click on element "${clickMatch[1]}"`)
      }
    }

    if (trimmed.includes('searchInput.fill(')) {
      const searchMatch = trimmed.match(/searchInput\.fill\(['"`]([^'"`]+)['"`]\)/)
      if (searchMatch) {
        actions.push(`Search for "${searchMatch[1]}"`)
      }
    }

    if (trimmed.includes('searchInput.press(')) {
      actions.push('Press Enter to submit search')
    }

    if (trimmed.includes('page.waitForLoadState')) {
      actions.push('Wait for page to load completely')
    }
  }

  if (actions.length === 0) {
    // Fallback: extract from test name or comments
    const testMatch = playwrightCode.match(/test\(['"`]([^'"`]+)['"`]/)
    if (testMatch) {
      return `Execute the following automation task: ${testMatch[1]}`
    }
    return 'Execute the provided Playwright automation script'
  }

  return `Execute the following automation steps:\n${actions.map((action, i) => `${i + 1}. ${action}`).join('\n')}`
}
